import { useState } from "react";

/**
 * Daily adherence over the selected window. One series, so no legend — the
 * card title names it. Days containing a missed dose take an amber marker and
 * are called out in the tooltip, never by colour alone.
 */
const SERIES = "#1d9e75";
const MISS = "#d97706";
const GRID = "#eef2f7";

const W = 720;
const H = 190;
const PAD = { top: 26, right: 20, bottom: 34, left: 44 };
const Y_MAX = 100;

/** Floor the axis just below the worst day, on a clean multiple of ten. */
const axisFloor = (points) => {
  const min = Math.min(...points.map((p) => p.pct));
  return Math.max(0, Math.min(90, Math.floor(min / 10) * 10 - 10));
};

const AdherenceChart = ({ series = [] }) => {
  const [hover, setHover] = useState(null);

  const points = series.filter((p) => p.pct !== null);
  if (points.length < 2) {
    return <p className="py-12 text-center text-xs text-gray-500">Not enough data yet for a trend.</p>;
  }

  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const yMin = axisFloor(points);
  const ticks = [Y_MAX, Math.round((Y_MAX + yMin) / 2), yMin];

  const x = (i) => PAD.left + (i / (points.length - 1)) * plotW;
  const y = (pct) => PAD.top + (1 - (Math.max(pct, yMin) - yMin) / (Y_MAX - yMin)) * plotH;

  const line = points.map((p, i) => `${x(i)},${y(p.pct)}`).join(" ");
  const area = `${PAD.left},${PAD.top + plotH} ${line} ${x(points.length - 1)},${PAD.top + plotH}`;
  const last = points[points.length - 1];
  const lastY = y(last.pct);
  const step = plotW / (points.length - 1);

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Daily adherence percentage">
        <defs>
          <linearGradient id="adherence-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={SERIES} stopOpacity="0.16" />
            <stop offset="100%" stopColor={SERIES} stopOpacity="0" />
          </linearGradient>
        </defs>

        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(tick)} y2={y(tick)} stroke={GRID} />
            <text x={PAD.left - 10} y={y(tick) + 4} textAnchor="end" fontSize="11" fill="#9ca3af">
              {tick}%
            </text>
          </g>
        ))}

        <polygon points={area} fill="url(#adherence-fill)" />
        <polyline
          points={line}
          fill="none"
          stroke={SERIES}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {points.map((p, i) => (
          <circle
            key={p.date}
            cx={x(i)}
            cy={y(p.pct)}
            r={p.missed ? 5 : 3.5}
            fill={p.missed ? MISS : SERIES}
            stroke="#fff"
            strokeWidth="2"
          />
        ))}

        <text
          x={x(points.length - 1)}
          y={lastY - PAD.top < 18 ? lastY + 20 : lastY - 12}
          textAnchor="end"
          fontSize="12"
          fontWeight="600"
          fill="#1a2e4a"
        >
          {last.pct}%
        </text>

        {points.map((p, i) => (
          <text key={`${p.date}-x`} x={x(i)} y={H - 10} textAnchor="middle" fontSize="11" fill="#9ca3af">
            {p.label}
          </text>
        ))}

        {hover !== null && (
          <line
            x1={x(hover)}
            x2={x(hover)}
            y1={PAD.top}
            y2={PAD.top + plotH}
            stroke="#cbd5e1"
            strokeDasharray="3 3"
          />
        )}

        {points.map((p, i) => (
          <rect
            key={`${p.date}-hit`}
            x={x(i) - step / 2}
            y="0"
            width={step}
            height={H}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
      </svg>

      {hover !== null && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-lg bg-primary-900 px-3 py-2 text-[11px] leading-tight text-white shadow-pop"
          style={{ left: `${(x(hover) / W) * 100}%`, top: `${(y(points[hover].pct) / H) * 100 - 4}%` }}
        >
          <p className="font-semibold">{points[hover].label}</p>
          <p className="opacity-80">{points[hover].pct}% adherence</p>
          {points[hover].missed > 0 && (
            <p className="mt-0.5 font-medium text-amber-300">
              {points[hover].missed} dose{points[hover].missed > 1 ? "s" : ""} missed
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AdherenceChart;
